import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ReactFlow, Background, Controls, MiniMap, useNodesState, useEdgesState, addEdge, MarkerType } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toast } from 'sonner';
import {
    ArrowLeft, Plus, Save, Download, MessageSquare, Code2, Image as ImageIcon,
    Link as LinkIcon, FileText, Trash2, Edit2, X
} from 'lucide-react';
import CompilerSidebar from '../components/CompilerSidebar';
import PromptLibrary from '../components/PromptLibrary';
import CommentPanel from '../components/CommentPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function WorkflowEditor() {
    const { workflowId } = useParams();
    const navigate = useNavigate();

    const [workflow, setWorkflow] = useState(null);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [showCompiler, setShowCompiler] = useState(false);
    const [showNodeEditor, setShowNodeEditor] = useState(false);
    const [showPromptLibrary, setShowPromptLibrary] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const [currentNode, setCurrentNode] = useState(null);
    const [saving, setSaving] = useState(false);

    // Node editor state
    const [nodeLabel, setNodeLabel] = useState('');
    const [nodeDescription, setNodeDescription] = useState('');
    const [nodeLink, setNodeLink] = useState('');
    const [nodeImage, setNodeImage] = useState('');
    const [selectedPrompt, setSelectedPrompt] = useState('');
    const [uploadingImage, setUploadingImage] = useState(false);

    useEffect(() => {
        fetchWorkflow();
    }, [workflowId]);

    const fetchWorkflow = async () => {
        try {
            const response = await fetch(`${API}/workflows/${workflowId}`, { credentials: 'include' });
            if (response.ok) {
                const data = await response.json();
                setWorkflow(data);

                if (data.nodes && data.nodes.length > 0) {
                    const flowNodes = data.nodes.map(node => ({
                        id: node.node_id,
                        position: node.position,
                        data: {
                            label: node.label,
                            description: node.description,
                            image_url: node.image_url,
                            link: node.link,
                            prompt_id: node.prompt_id
                        },
                        style: {
                            background: 'rgba(17, 17, 17, 0.9)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '12px',
                            padding: '16px',
                            color: '#fff',
                            minWidth: '200px'
                        }
                    }));
                    setNodes(flowNodes);
                }

                if (data.connections && data.connections.length > 0) {
                    const flowEdges = data.connections.map((conn, idx) => ({
                        id: `e${conn.source}-${conn.target}-${idx}`,
                        source: conn.source,
                        target: conn.target,
                        label: conn.label,
                        animated: true,
                        style: { stroke: '#00E599' },
                        markerEnd: { type: MarkerType.ArrowClosed, color: '#00E599' }
                    }));
                    setEdges(flowEdges);
                }
            }
        } catch (error) {
            toast.error('Failed to load workflow');
            navigate('/dashboard');
        }
    };

    const onConnect = useCallback((params) => {
        setEdges((eds) => addEdge({ ...params, animated: true, style: { stroke: '#00E599' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#00E599' } }, eds));
    }, [setEdges]);

    const addNode = () => {
        const newNode = {
            id: `node_${Date.now()}`,
            position: { x: Math.random() * 400 + 100, y: Math.random() * 400 + 100 },
            data: { label: 'New Node' },
            style: {
                background: 'rgba(17, 17, 17, 0.9)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '16px',
                color: '#fff',
                minWidth: '200px'
            }
        };
        setNodes((nds) => [...nds, newNode]);
    };

    const editNode = (node) => {
        setCurrentNode(node);
        setNodeLabel(node.data.label || '');
        setNodeDescription(node.data.description || '');
        setNodeLink(node.data.link || '');
        setNodeImage(node.data.image_url || '');
        setSelectedPrompt(node.data.prompt_id || '');
        setShowNodeEditor(true);
    };

    const saveNodeEdits = () => {
        if (!currentNode) return;

        setNodes((nds) =>
            nds.map((node) =>
                node.id === currentNode.id
                    ? {
                        ...node,
                        data: {
                            ...node.data,
                            label: nodeLabel,
                            description: nodeDescription,
                            link: nodeLink,
                            image_url: nodeImage,
                            prompt_id: selectedPrompt
                        }
                    }
                    : node
            )
        );

        setShowNodeEditor(false);
        toast.success('Node updated');
    };

    const deleteNode = (nodeId) => {
        setNodes((nds) => nds.filter((node) => node.id !== nodeId));
        setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
        toast.success('Node deleted');
    };

    const uploadImage = async (file) => {
        if (!file) return;

        setUploadingImage(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await fetch(`${API}/upload`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            if (response.ok) {
                const data = await response.json();
                const fullUrl = process.env.REACT_APP_BACKEND_URL + data.url;
                setNodeImage(fullUrl);
                toast.success('Image uploaded');
            }
        } catch (error) {
            toast.error('Failed to upload image');
        } finally {
            setUploadingImage(false);
        }
    };

    const saveWorkflow = async () => {
        setSaving(true);
        try {
            const workflowNodes = nodes.map(node => ({
                node_id: node.id,
                label: node.data.label,
                description: node.data.description || null,
                image_url: node.data.image_url || null,
                link: node.data.link || null,
                prompt_id: node.data.prompt_id || null,
                position: node.position,
                data: {}
            }));

            const workflowConnections = edges.map(edge => ({
                source: edge.source,
                target: edge.target,
                label: edge.label || null
            }));

            const response = await fetch(`${API}/workflows/${workflowId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    nodes: workflowNodes,
                    connections: workflowConnections
                })
            });

            if (response.ok) {
                toast.success('Workflow saved!');
            }
        } catch (error) {
            toast.error('Failed to save workflow');
        } finally {
            setSaving(false);
        }
    };

    const exportWorkflow = async (format) => {
        try {
            const response = await fetch(`${API}/workflows/${workflowId}/export/${format}`, {
                credentials: 'include'
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${workflow.name}.${format}`;
                a.click();
                toast.success(`Exported as ${format.toUpperCase()}`);
            }
        } catch (error) {
            toast.error('Export failed');
        }
    };

    const onNodeClick = useCallback((event, node) => {
        editNode(node);
    }, []);

    if (!workflow) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="glass-card p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-text-secondary">Loading workflow...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen bg-background text-white flex flex-col overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-black/50 backdrop-blur-lg z-10">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="font-heading text-xl font-bold">{workflow.name}</h1>
                        <p className="text-xs text-text-muted capitalize">{workflow.type} Workflow</p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button onClick={addNode} className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
                        <Plus className="w-4 h-4" />
                        Add Node
                    </button>

                    <button onClick={() => setShowPromptLibrary(true)} className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
                        <FileText className="w-4 h-4" />
                        Prompts
                    </button>

                    <button onClick={() => setShowComments(!showComments)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${showComments ? 'bg-primary/20 text-primary' : 'bg-white/5 hover:bg-white/10'}`}>
                        <MessageSquare className="w-4 h-4" />
                        Comments
                    </button>

                    <button onClick={() => setShowCompiler(!showCompiler)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${showCompiler ? 'bg-primary text-black' : 'bg-white/5 hover:bg-white/10'}`}>
                        <Code2 className="w-4 h-4" />
                        IDE
                    </button>

                    <div className="h-6 w-px bg-white/10" />

                    <button onClick={saveWorkflow} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50 font-medium">
                        <Save className="w-4 h-4" />
                        {saving ? 'Saving...' : 'Save'}
                    </button>

                    <button onClick={() => exportWorkflow('pdf')} className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
                        <Download className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Canvas */}
            <div className="flex-1 relative">
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onNodeClick={onNodeClick}
                    fitView
                    className="bg-black"
                >
                    <Background color="rgba(255, 255, 255, 0.05)" gap={20} />
                    <Controls />
                    <MiniMap
                        nodeColor="#00E599"
                        maskColor="rgba(0, 0, 0, 0.8)"
                        style={{ background: '#0A0A0A', border: '1px solid rgba(255, 255, 255, 0.1)' }}
                    />
                </ReactFlow>
            </div>

            {/* Overlays */}
            {showCompiler && <CompilerSidebar onClose={() => setShowCompiler(false)} />}

            {showComments && (
                <CommentPanel workflowId={workflowId} onClose={() => setShowComments(false)} />
            )}

            {showPromptLibrary && (
                <PromptLibrary
                    onClose={() => setShowPromptLibrary(false)}
                    onSelect={(prompt) => {
                        if (showNodeEditor) {
                            setNodeDescription(prev => (prev ? prev + '\n\n' : '') + prompt.content);
                            setSelectedPrompt(prompt.prompt_id);
                        } else {
                            navigator.clipboard.writeText(prompt.content);
                            toast.success('Prompt copied');
                        }
                        setShowPromptLibrary(false);
                    }}
                />
            )}

            {/* Node Editor Modal */}
            {showNodeEditor && currentNode && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="glass-card p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-heading text-2xl font-bold">Edit Node</h2>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => {
                                        deleteNode(currentNode.id);
                                        setShowNodeEditor(false);
                                    }}
                                    className="p-2 hover:bg-white/5 rounded-lg transition-colors text-accent-pink"
                                >
                                    <Trash2 className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => setShowNodeEditor(false)}
                                    className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">Label</label>
                                <input
                                    type="text"
                                    value={nodeLabel}
                                    onChange={(e) => setNodeLabel(e.target.value)}
                                    className="glass-input w-full"
                                />
                            </div>

                            <div>
                                <div className="flex justify-between mb-2">
                                    <label className="block text-sm font-medium">Description</label>
                                    <button
                                        onClick={() => setShowPromptLibrary(true)}
                                        className="text-primary text-xs hover:underline flex items-center gap-1"
                                    >
                                        <Plus className="w-3 h-3" /> Add Prompt
                                    </button>
                                </div>
                                <textarea
                                    value={nodeDescription}
                                    onChange={(e) => setNodeDescription(e.target.value)}
                                    className="glass-input w-full h-24 resize-none"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Link</label>
                                <div className="relative">
                                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                                    <input
                                        type="url"
                                        value={nodeLink}
                                        onChange={(e) => setNodeLink(e.target.value)}
                                        className="glass-input w-full pl-10"
                                        placeholder="https://"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Image</label>
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                                        <input
                                            type="text"
                                            value={nodeImage}
                                            onChange={(e) => setNodeImage(e.target.value)}
                                            className="glass-input w-full pl-10"
                                            placeholder="Image URL"
                                        />
                                    </div>
                                    <label className="px-4 py-3 bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-colors flex items-center gap-2 whitespace-nowrap">
                                        {uploadingImage ? 'Uploading...' : 'Upload'}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])}
                                            className="hidden"
                                            disabled={uploadingImage}
                                        />
                                    </label>
                                </div>
                                {nodeImage && (
                                    <img src={nodeImage} alt="Preview" className="mt-2 w-full h-48 object-cover rounded-lg border border-white/10" />
                                )}
                            </div>
                        </div>

                        <div className="mt-8 flex justify-end gap-3">
                            <button
                                onClick={() => setShowNodeEditor(false)}
                                className="px-6 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={saveNodeEdits}
                                className="neon-button"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default WorkflowEditor;
